import { Button } from '@/components/ui/button';
import { BookOpen, Sparkles, TrendingUp, Info, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSaveToTBR } from '@/hooks/useSaveToTBR';
import { toast } from 'sonner';
import { CoverImage } from './CoverImage';
import { useRecommendationFeedback } from '@/hooks/useRecommendationFeedback';
import { useState } from 'react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { QuickReviewDialog } from './QuickReviewDialog';
import { ReadingFormat } from '@/lib/readingFormatUtils';

interface BookRecommendationCardProps {
  title: string;
  author: string;
  reason?: string;
  bookId?: string;
  isbn13?: string;
  isbn10?: string;
  coverUrl?: string;
  confidenceScore?: number; // 0-100
  matchDetails?: string; // Specific examples from user's history
  sourceBooks?: Array<{
    id: string;
    title: string;
    reason: string;
  }>;
  recommendationType?: 'safe_bet' | 'stretch' | 'wildcard' | 'classic';
  onDismiss?: () => void;
}

export function BookRecommendationCard({ 
  title, 
  author,
  reason,
  bookId,
  isbn13,
  isbn10,
  coverUrl,
  confidenceScore,
  matchDetails,
  sourceBooks,
  recommendationType,
  onDismiss
}: BookRecommendationCardProps) {
  const navigate = useNavigate();
  const { toggleSave, loading: isSaving, isSaved } = useSaveToTBR(
    bookId || '', 
    undefined, 
    { title, author, isbn13, isbn10 }
  );
  const { submitFeedback, loading: feedbackLoading } = useRecommendationFeedback();
  const [dismissed, setDismissed] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewBookId, setReviewBookId] = useState<string | undefined>(bookId);
  const isMobile = useIsMobile();

  const handleAddToTBR = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    await toggleSave();
    
    // Log upvote feedback when adding to TBR
    await submitFeedback({
      bookId,
      bookTitle: title,
      bookAuthor: author,
      isbn13,
      isbn10,
      feedbackType: 'upvote',
      recommendationReason: reason,
    });
  };

  const handleViewDetails = async () => {
    try {
      if (bookId) {
        navigate(`/book/${bookId}`);
        return;
      }

      // Try to find existing book by ISBNs
      if (isbn13 || isbn10) {
        const orFilters = [
          isbn13 ? `isbn13.eq.${isbn13}` : null,
          isbn10 ? `isbn10.eq.${isbn10}` : null,
        ].filter(Boolean).join(',');

        const { data: existing, error: findError } = await supabase
          .from('books')
          .select('id')
          .or(orFilters)
          .limit(1)
          .maybeSingle();

        if (findError) throw findError;
        if (existing?.id) {
          navigate(`/book/${existing.id}`);
          return;
        }
      }

      // Create minimal book record then navigate
      const { data: created, error: insertError } = await supabase
        .from('books')
        .insert({
          title,
          authors: author ? [author] : [],
          isbn13: isbn13 || null,
          isbn10: isbn10 || null,
          cover_url: coverUrl || null,
          source: 'openlibrary',
          language: 'en',
          enrichment_status: 'pending',
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      navigate(`/book/${created.id}`);
    } catch (err) {
      console.error('Error opening book from rec card', err);
      toast.error('Unable to open book', {
        description: 'Please try again in a moment.',
      });
    }
  };

  const handleDismiss = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Submit downvote feedback and dismiss
    const result = await submitFeedback({
      bookId,
      bookTitle: title,
      bookAuthor: author,
      isbn13,
      isbn10,
      feedbackType: 'downvote',
      recommendationReason: reason,
    });

    if (result.success) {
      setDismissed(true);
      onDismiss?.();
    }
  };

  if (dismissed) {
    return null;
  }

  const handleAlreadyRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      // If we already have a bookId, use it
      if (bookId) {
        setReviewBookId(bookId);
        setReviewDialogOpen(true);
        return;
      }

      // Try to find existing book by ISBNs
      if (isbn13 || isbn10) {
        const orFilters = [
          isbn13 ? `isbn13.eq.${isbn13}` : null,
          isbn10 ? `isbn10.eq.${isbn10}` : null,
        ].filter(Boolean).join(',');

        const { data: existing, error: findError } = await supabase
          .from('books')
          .select('id')
          .or(orFilters)
          .limit(1)
          .maybeSingle();

        if (findError) throw findError;
        if (existing?.id) {
          setReviewBookId(existing.id);
          setReviewDialogOpen(true);
          return;
        }
      }

      // Create minimal book record
      const { data: created, error: insertError } = await supabase
        .from('books')
        .insert({
          title,
          authors: author ? [author] : [],
          isbn13: isbn13 || null,
          isbn10: isbn10 || null,
          cover_url: coverUrl || null,
          source: 'openlibrary',
          language: 'en',
          enrichment_status: 'pending',
        })
        .select('id')
        .single();

      if (insertError) throw insertError;
      setReviewBookId(created.id);
      setReviewDialogOpen(true);
    } catch (err) {
      console.error('Error preparing book for review', err);
      toast.error('Unable to open review dialog', {
        description: 'Please try again in a moment.',
      });
    }
  };

  const handleReviewSubmit = async (data: {
    rating: number;
    reviewText: string;
    finishedAt?: Date;
    startedAt?: Date;
    privacy: 'public' | 'private';
    tags?: string[];
    reading_format?: ReadingFormat;
  }) => {
    if (!reviewBookId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create/update user_books entry
      // Update existing or insert new user_books record without relying on onConflict
      const updateData: any = {
        shelf: 'read',
        rating_star: data.rating,
        review_text: data.reviewText,
        privacy: data.privacy,
        tags: data.tags || [],
        reading_format: data.reading_format,
        finished_at: data.finishedAt
          ? data.finishedAt.toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
      };

      if (data.startedAt) {
        updateData.started_at = data.startedAt.toISOString().split('T')[0];
      }

      const { data: updatedRows, error: updateError } = await supabase
        .from('user_books')
        .update(updateData)
        .eq('user_id', user.id)
        .eq('book_id', reviewBookId)
        .select('id');

      if (updateError) throw updateError;

      if (!updatedRows || updatedRows.length === 0) {
        const { error: insertError } = await supabase
          .from('user_books')
          .insert({
            user_id: user.id,
            book_id: reviewBookId,
            ...updateData,
          });
        if (insertError) throw insertError;
      }

      // Log positive feedback
      await submitFeedback({
        bookId: reviewBookId,
        bookTitle: title,
        bookAuthor: author,
        isbn13,
        isbn10,
        feedbackType: 'upvote',
        recommendationReason: reason,
      });

      toast.success('Review saved!');
      setReviewDialogOpen(false);
      setDismissed(true);
    } catch (err) {
      console.error('Error saving review:', err);
      toast.error('Failed to save review', {
        description: 'Please try again.',
      });
    }
  };

  // Shared details content for both hover card and sheet
  const DetailsContent = () => (
    <div className="space-y-3 max-w-full pr-2">
      {sourceBooks && sourceBooks.length > 0 && (
        <div className="flex gap-2">
          <BookOpen className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1 max-w-full">
            <p className="text-xs font-medium">Because you read</p>
            {sourceBooks.slice(0, 2).map((source, idx) => (
              <div key={idx} className="text-xs text-muted-foreground leading-relaxed break-words max-w-full" style={{ textWrap: 'pretty' }}>
                <span className="font-medium">{source.title}</span>
                {source.reason && <span> — {source.reason}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {reason && (
        <div className="flex gap-2 pt-2 border-t">
          <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1.5 max-w-full">
            <p className="text-xs font-medium">Why we recommend this</p>
            <p className="text-xs text-muted-foreground leading-relaxed break-words max-w-full whitespace-pre-wrap" style={{ textWrap: 'pretty' }}>
              {reason}
            </p>
          </div>
        </div>
      )}
      
      {matchDetails && (
        <div className="flex gap-2 pt-2 border-t">
          <TrendingUp className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1.5 max-w-full">
            <p className="text-xs font-medium">Based on your history</p>
            <p className="text-xs text-muted-foreground leading-relaxed break-words max-w-full whitespace-pre-wrap" style={{ textWrap: 'pretty' }}>
              {matchDetails}
            </p>
          </div>
        </div>
      )}
      
      {confidenceScore !== undefined && (
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">Match confidence</span>
            <span className="font-medium">{confidenceScore}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all"
              style={{ width: `${confidenceScore}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );

  const hasDetails = reason || matchDetails || sourceBooks;

  // Shared card content
  const CardContent = () => (
    <>
      <div className="aspect-[2/3] relative overflow-hidden rounded-lg mb-3 bg-muted">
        {coverUrl || bookId || isbn13 || isbn10 ? (
          <CoverImage
            bookId={bookId}
            isbn13={isbn13}
            isbn10={isbn10}
            title={title}
            author={author}
            coverUrl={coverUrl}
            size="sm"
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-8 h-8 text-muted-foreground" />
          </div>
        )}
        
        {confidenceScore !== undefined && (
          <Badge 
            variant={confidenceScore >= 80 ? "default" : confidenceScore >= 60 ? "secondary" : "outline"}
            className="absolute top-2 right-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {confidenceScore}% match
          </Badge>
        )}
        
        {recommendationType === 'wildcard' && (
          <Badge 
            variant="outline" 
            className="absolute bottom-2 left-2 text-xs bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-950/50 dark:text-purple-300 dark:border-purple-700"
          >
            🎲 Wild Card
          </Badge>
        )}
      </div>
      
      <div className="flex-1 flex flex-col">
        <h3 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors mb-1">
          {title}
        </h3>
        
        <p className="text-xs text-muted-foreground mb-3 line-clamp-1">
          {author}
        </p>

        <div className="mt-auto space-y-2">
          <Button
            size="sm"
            variant={isSaved ? "outline" : "default"}
            onClick={handleAddToTBR}
            disabled={isSaving}
            className="w-full text-xs"
          >
            {isSaved ? 'In TBR' : 'Add to TBR'}
          </Button>
          
          <Button
            size="sm"
            variant="outline"
            onClick={handleAlreadyRead}
            className="w-full text-xs"
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Already Read
          </Button>
          
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            disabled={feedbackLoading}
            className="w-full text-xs text-muted-foreground hover:text-foreground"
          >
            Not interested
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile: Sheet with info button */}
      {isMobile && hasDetails ? (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <div className="relative">
            <div className="group cursor-pointer flex flex-col h-full" onClick={handleViewDetails}>
              <CardContent />
            </div>
            <SheetTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="absolute top-2 left-2 h-6 w-6"
                onClick={(e) => e.stopPropagation()}
              >
                <Info className="h-4 w-4" />
              </Button>
            </SheetTrigger>
          </div>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto px-4 pb-safe-bottom">
            <SheetHeader>
              <SheetTitle>{title}</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <DetailsContent />
            </div>
          </SheetContent>
        </Sheet>
      ) : !isMobile && hasDetails ? (
        /* Desktop: HoverCard */
        <HoverCard openDelay={200}>
          <HoverCardTrigger asChild>
            <div className="group cursor-pointer flex flex-col h-full" onClick={handleViewDetails}>
              <CardContent />
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="w-[480px] max-w-[calc(100vw-2rem)]" side="top">
            <DetailsContent />
          </HoverCardContent>
        </HoverCard>
      ) : (
        /* No details - just the card */
        <div className="group cursor-pointer flex flex-col h-full" onClick={handleViewDetails}>
          <CardContent />
        </div>
      )}

      {/* Review Dialog */}
      {reviewBookId && (
        <QuickReviewDialog
          isOpen={reviewDialogOpen}
          onClose={() => setReviewDialogOpen(false)}
          book={{
            id: reviewBookId,
            title,
            author,
          }}
          onSubmit={handleReviewSubmit}
        />
      )}
    </>
  );
}
